import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { HiCheck, HiPlus } from 'react-icons/hi';
import TagPill from '../../ui/TagPill';
import styles from './TagDropdown.module.css';

interface TagDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  availableTags: string[];
  assignedTags: string[];
  onTagToggle: (tag: string, isCurrentlyAssigned: boolean) => void;
  onCreateTag?: (tag: string) => Promise<void>;
  anchorRef?: React.RefObject<HTMLElement>;
  emptyMessage?: string;
  createPlaceholder?: string;
}

const TagDropdown: React.FC<TagDropdownProps> = ({
  isOpen,
  onClose,
  availableTags,
  assignedTags,
  onTagToggle,
  onCreateTag,
  anchorRef,
  emptyMessage = 'No segments available',
  createPlaceholder = 'Create new tag...',
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [newTagName, setNewTagName] = useState('');
  const [creating, setCreating] = useState(false);

  // Sort tags: assigned first, then unassigned
  const sortedTags = [...availableTags].sort((a, b) => {
    const aAssigned = assignedTags.includes(a);
    const bAssigned = assignedTags.includes(b);
    if (aAssigned && !bAssigned) return -1;
    if (!aAssigned && bAssigned) return 1;
    return a.localeCompare(b);
  });

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        (!anchorRef?.current || !anchorRef.current.contains(target))
      ) {
        onClose();
      }
    };

    // Escape key to close
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, anchorRef]);

  // Position dropdown relative to anchor
  useEffect(() => {
    if (!isOpen || !anchorRef?.current || !dropdownRef.current) return;

    const anchor = anchorRef.current;
    const dropdown = dropdownRef.current;
    const rect = anchor.getBoundingClientRect();

    // Position below the anchor
    dropdown.style.top = `${rect.bottom + 4}px`;
    dropdown.style.left = `${rect.left}px`;

    // Adjust if goes off screen to the right
    const dropdownRect = dropdown.getBoundingClientRect();
    if (dropdownRect.right > window.innerWidth - 16) {
      dropdown.style.left = `${window.innerWidth - dropdownRect.width - 16}px`;
    }

    // Adjust if goes off screen to the bottom
    if (dropdownRect.bottom > window.innerHeight - 16) {
      dropdown.style.top = `${rect.top - dropdownRect.height - 4}px`;
    }
  }, [isOpen, anchorRef]);

  const handleCreateTag = async () => {
    const trimmed = newTagName.trim();
    if (!trimmed || creating || !onCreateTag) return;
    if (availableTags.includes(trimmed)) {
      // Tag already exists, just toggle it on
      onTagToggle(trimmed, false);
      setNewTagName('');
      return;
    }
    setCreating(true);
    await onCreateTag(trimmed);
    setCreating(false);
    setNewTagName('');
  };

  const handleCreateKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateTag();
    }
  };

  if (!isOpen) return null;

  const dropdown = (
    <div ref={dropdownRef} className={styles.dropdown}>
      {onCreateTag && (
        <div className={styles.createSection}>
          <input
            type="text"
            className={styles.createInput}
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyDown={handleCreateKeyDown}
            placeholder={createPlaceholder}
            disabled={creating}
          />
          <button
            type="button"
            className={styles.createButton}
            onClick={handleCreateTag}
            disabled={!newTagName.trim() || creating}
          >
            {/* @ts-ignore - react-icons compatibility with React 19 */}
            <HiPlus size={16} />
          </button>
        </div>
      )}
      {sortedTags.length === 0 && !onCreateTag ? (
        <div className={styles.emptyMessage}>{emptyMessage}</div>
      ) : sortedTags.length === 0 ? (
        <div className={styles.emptyMessage}>{emptyMessage}</div>
      ) : (
        <div className={styles.tagList}>
          {sortedTags.map((tag) => {
            const isAssigned = assignedTags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                className={`${styles.tagItem} ${isAssigned ? styles.assigned : ''}`}
                onClick={() => onTagToggle(tag, isAssigned)}
              >
                <TagPill tag={tag} size="small" />
                {isAssigned && (
                  /* @ts-ignore - react-icons compatibility with React 19 */
                  <HiCheck className={styles.checkIcon} size={16} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return createPortal(dropdown, document.body);
};

export default TagDropdown;
